import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, LoaderCircle, Plus, Save } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import type { Equipo } from "@/lib/equipos";
import type { Fuente } from "@/lib/fuentes";

// Espejo de la validación de servidor (factory/punto_dto.go): nombre obligatorio ≤60,
// descripción obligatoria ≤500, equipo obligatorio. id_fuente es opcional (0 ⇒ automática:
// el servidor usa la primera fuente del equipo o auto-crea una por defecto). Los ids se
// coaccionan a número porque el <select> nativo entrega string.
const puntoSchema = z.object({
  id_equipo: z.coerce.number().int().positive("Selecciona un equipo"),
  id_fuente: z.coerce.number().int().nonnegative(),
  nombre: z.string().trim().min(1, "El nombre es obligatorio").max(60, "Máximo 60 caracteres"),
  descripcion: z
    .string()
    .trim()
    .min(1, "La descripción es obligatoria")
    .max(500, "Máximo 500 caracteres"),
  activo: z.boolean(),
});

// El esquema coacciona id_equipo e id_fuente (z.coerce.number): el tipo de entrada
// difiere del de salida ya validado. Con @hookform/resolvers v5 hay que tipar useForm
// con ambos.
type PuntoFormInput = z.input<typeof puntoSchema>;
export type PuntoFormValues = z.output<typeof puntoSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<PuntoFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.code === "CODIGO_DUPLICADO") {
      setError("nombre", { message: "Ya existe un punto con ese nombre en la fuente." });
      return;
    }
    if (err.code === "VALIDACION" && err.fields) {
      let mapped = false;
      for (const campo of ["nombre", "descripcion", "id_equipo", "id_fuente"] as const) {
        const msg = err.fields[campo];
        if (msg) {
          setError(campo, { message: msg });
          mapped = true;
        }
      }
      if (mapped) {
        return;
      }
    }
    setFormError(err.message || "No se pudo guardar el punto.");
    return;
  }
  setFormError("No se pudo guardar el punto. Comprueba tu conexión.");
}

/**
 * PuntoForm es el formulario reutilizable de alta/edición de puntos de aislamiento
 * (react-hook-form + zod). En alta permite elegir equipo y (opcionalmente) fuente: si se
 * deja «Automática», el servidor usa la primera fuente del equipo o auto-crea una por
 * defecto. Ofrece «Guardar y añadir otro al mismo equipo» (chained-add), que conserva el
 * equipo y la fuente y limpia el nombre/descripción. En edición equipo y fuente quedan
 * fijos (la fuente padre es inmutable).
 */
export function PuntoForm({
  defaultValues,
  equipos,
  fuentes,
  codigoFuente,
  submitLabel,
  showAddAnother,
  pending,
  onSubmit,
  onCancel,
}: {
  defaultValues: PuntoFormValues;
  equipos: Equipo[];
  fuentes: Fuente[];
  codigoFuente?: string;
  submitLabel: string;
  showAddAnother: boolean;
  pending: boolean;
  onSubmit: (values: PuntoFormValues, addAnother: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const isEdit = codigoFuente !== undefined;
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PuntoFormInput, unknown, PuntoFormValues>({
    resolver: zodResolver(puntoSchema),
    defaultValues,
  });

  const idEquipo = Number(watch("id_equipo"));
  const fuentesDelEquipo = fuentes.filter((f) => f.id_equipo === idEquipo);
  const equipoSinFuentes = idEquipo > 0 && fuentesDelEquipo.length === 0;

  const equipoField = register("id_equipo");

  function run(addAnother: boolean) {
    return handleSubmit(async (values) => {
      setFormError(null);
      try {
        await onSubmit(values, addAnother);
        if (addAnother) {
          // Encadena otro punto al MISMO equipo/fuente: conserva el contexto, limpia nombre
          // y descripción.
          reset({ ...values, nombre: "", descripcion: "" });
        }
      } catch (err) {
        aplicarError(err, setError, setFormError);
      }
    });
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={run(false)} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_equipo">Equipo</Label>
        <Select
          id="id_equipo"
          disabled={isEdit}
          aria-invalid={errors.id_equipo ? true : undefined}
          {...equipoField}
          onChange={(e) => {
            void equipoField.onChange(e);
            // Al cambiar de equipo, la fuente elegida deja de ser válida: vuelve a Automática.
            setValue("id_fuente", 0);
          }}
        >
          <option value="0" disabled>
            Selecciona un equipo
          </option>
          {equipos.map((equipo) => (
            <option key={equipo.id} value={equipo.id}>
              {equipo.codigo_equipo} · {equipo.nombre_equipo}
            </option>
          ))}
        </Select>
        {isEdit ? (
          <p className="text-slate-500 text-sm">El equipo de un punto no se puede cambiar.</p>
        ) : null}
        <FieldError>{errors.id_equipo?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="id_fuente">Fuente</Label>
        <Select
          id="id_fuente"
          disabled={isEdit}
          aria-invalid={errors.id_fuente ? true : undefined}
          {...register("id_fuente")}
        >
          <option value="0">Automática (primera fuente del equipo)</option>
          {fuentesDelEquipo.map((fuente) => (
            <option key={fuente.id} value={fuente.id}>
              {fuente.codigo_fuente} · {fuente.nombre_fuente}
            </option>
          ))}
        </Select>
        {isEdit ? (
          <p className="text-slate-500 text-sm">La fuente de un punto no se puede cambiar.</p>
        ) : equipoSinFuentes ? (
          <p className="text-slate-500 text-sm">
            Este equipo no tiene fuentes: se creará una por defecto automáticamente.
          </p>
        ) : null}
        <FieldError>{errors.id_fuente?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="nombre">Nombre</Label>
        <Input
          id="nombre"
          autoComplete="off"
          aria-invalid={errors.nombre ? true : undefined}
          {...register("nombre")}
        />
        <FieldError>{errors.nombre?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="descripcion">Descripción</Label>
        <Textarea
          id="descripcion"
          aria-invalid={errors.descripcion ? true : undefined}
          {...register("descripcion")}
        />
        <FieldError>{errors.descripcion?.message}</FieldError>
      </div>

      <label className="flex items-center gap-3">
        <input
          type="checkbox"
          className="h-5 w-5 shrink-0 rounded border-slate-300 text-primary-600 focus-visible:ring-2 focus-visible:ring-primary-500"
          {...register("activo")}
        />
        <span className="text-base text-slate-800">Activo</span>
      </label>

      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        {showAddAnother ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => void run(true)()}
            disabled={pending}
          >
            <Plus className="h-5 w-5" aria-hidden="true" />
            Guardar y añadir otro
          </Button>
        ) : null}
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
